/* eslint-disable max-lines-per-function */
import * as turf from '@turf/turf';
import { ArrowLeft, Eye, EyeOff, Move, Settings04, Target03 } from '@untitled-ui/icons-react/build/cjs';
import mapboxgl from 'mapbox-gl';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import OrderHeaderActions from 'src/app/Orders/Common/OrderHeaderActions';
import ButtonCmp from 'src/components/ButtonCmp';
import FilterDropdown from 'src/components/FilterDropdown';
import PageSectionLayout from 'src/components/PageSectionLayout';
import Header from 'src/components/PageSectionLayout/Header/Header';
import TooltipCmp from 'src/components/TooltipCmp';
import WidgetSettingsModal from 'src/components/WidgetSettingsModal';
import { IWidget } from 'src/components/WidgetSettingsModal/widgetSettings.interface';
import { ORDER_STATUS } from 'src/constants/common';
import { PATH } from 'src/constants/path';
import { useOrderLiveSync } from 'src/hooks/useOrderLiveSync';
import { currentUser, setCurrentUser } from 'src/redux/auth.slice';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { getOrderDetailsAsync, resetOrderDetailsState } from 'src/redux/orders.slice';
import { updateUserWidgets } from 'src/services/AuthService';
import { DrivingRouteResult, getDrivingRouteWithWaypoints, getRemainingRouteMiles } from 'src/utils/RouteUtils';
import { transformWidgetsToApiFormat, WIDGET_SECTION_TYPE } from 'src/utils/widgetSettings.utils';
import 'mapbox-gl/dist/mapbox-gl.css';

import { useRadarData } from '../hooks/useRadarData';
import useTrackHeaderTheme from '../hooks/useTrackHeaderTheme';
import { getLatestStartedLegLocation, getStartedLegLocationUpdates } from '../track.helpers';
import { ITrackNavState } from '../track.interface';
import { selectActiveScheduleSection, setActiveScheduleSection, setRemainingDistance } from '../track.slice';
import TrackHeaderClock from '../TrackHeaderClock';
import { bumpTrackRouteRequestSeq, getActiveTrackView, getTrackRouteRequestSeq, setCameFromDetail, setActiveTrackView, useTrackMap } from '../TrackMapContext';
import TrackMapThemePicker from '../TrackMapThemePicker';
import TrackTimezoneToggle from '../TrackTimezoneToggle';

import OrderMetaCards from './OrderMetaCards';
import RadarMapAboveComponentNew from './RadarMapAboveComponentNew';
import { DEFAULT_RADAR_WIDGET_SETTINGS, RADAR_WIDGET_IDS, transformRadarWidgets } from './radarWidgetSettings.helper';
import CarrierDetailsSection from './sections/CarrierDetailsSection';
import DeliveredStateSection from './sections/DeliveredStateSection';
import InternalChatSection from './sections/InternalChatSection';
import InTransitStateSection from './sections/InTransitStateSection';
import LatestActivitySection from './sections/LatestActivitySection';
import LocationUpdatesSection from './sections/LocationUpdatesSection';
import OrderDetailsSection from './sections/OrderDetailsSection';
import PickupStateSection from './sections/PickupStateSection';

const RadarOrderDetailNew = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const { state } = useLocation();
    const navigate = useNavigate();
    // orderStatus carried over from the list row / card / map popup click — used ONLY as
    // the first-paint hint for resolvedScheduleSection while the detail API loads
    // (trackOrderDetail is null during that window). Once it arrives, its status takes over.
    const navStateOrderStatus = (state as ITrackNavState | null)?.orderStatus;
    const { fetchOrderDetail, clearOrderDetail, trackOrderDetail, isDetailLoading } = useRadarData();
    const dispatch = useAppDispatch();
    const user = useAppSelector(currentUser);

    const activeScheduleSection = useAppSelector(selectActiveScheduleSection);

    const resolvedScheduleSection: 'pickup' | 'inTransit' | 'delivery' = useMemo(() => {
        if (activeScheduleSection) return activeScheduleSection;

        // Prefer the loaded detail's status; fall back to the orderStatus passed via nav
        // state from the list row / card / map popup so the correct section shows on the
        // FIRST paint instead of flashing 'delivery' (the fallback) while the API loads.
        const effectiveOrderStatus = trackOrderDetail?.orderStatus ?? navStateOrderStatus;

        if (effectiveOrderStatus === ORDER_STATUS.DISPATCHED) return 'pickup';
        if (effectiveOrderStatus === ORDER_STATUS.IN_TRANSIT) return 'inTransit';

        return 'delivery';
    }, [activeScheduleSection, trackOrderDetail?.orderStatus, navStateOrderStatus]);

    const scheduleOptions = useMemo(
        () => [
            { value: 'pickup', label: t('Radar.Pickup_Schedule') as string },
            { value: 'inTransit', label: t('Radar.In_Transit_Schedule') as string },
            { value: 'delivery', label: t('Radar.Delivery_Schedule') as string },
        ],
        [t]
    );

    const handleScheduleSelect = useCallback(
        (value: string) => {
            dispatch(setActiveScheduleSection(value as 'pickup' | 'inTransit' | 'delivery'));
        },
        [dispatch]
    );

    const scheduleSwitcher = (
        <FilterDropdown
            options={scheduleOptions}
            selectedValue={resolvedScheduleSection}
            onSelect={handleScheduleSelect}
            hideOnSingleOption={false}
            labelClassName="text-sm font-medium text-grayLight900"
            dropdownPosition="left"
            isDefaultLabelColor={false}
            isLoading={isDetailLoading}
        />
    );

    // Shared persistent Mapbox instance from TrackMapLayout.
    const { mapRef, isMapReady, attachMapTo, returnToStash, styleEpoch } = useTrackMap();
    // Theme-adaptive header classes (transparent wash + white-title flip on dark
    // backdrops) — shared with the list map view via useTrackHeaderTheme.
    // Detail page always renders flat (mercator) — globe is a list-view-only feature —
    // so the border-bottom color always resolves to the flat-projection variant.
    const trackHeaderThemeClass = useTrackHeaderTheme('mercator');

    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    // True after this component's map setup has run (map attached + residue cleared).
    // Separate from isMapReady (context) because mapReadyRef guards updateRouteAndMarkers
    // which must NOT run before attachMapTo and the source cleanup have completed.
    const mapReadyRef = useRef<boolean>(false);
    const routeBoundsRef = useRef<mapboxgl.LngLatBounds | null>(null);
    const animationFrameRef = useRef<number | null>(null);
    const animationTimeoutRef = useRef<number | null>(null);
    const currentMarkerPositionRef = useRef<[number, number] | null>(null);
    // Tracks which marker style (default/inTransit/completed) is currently rendered so we skip
    // unnecessary rebuilds that would cause tooltip blink on status-only changes.
    const completedMarkerStyleRef = useRef<string>('');

    const markersRef = useRef<{
        completed: mapboxgl.Marker[];
        current: mapboxgl.Marker | null;
    }>({ completed: [], current: null });

    const [isVisible, setIsVisible] = useState(true);
    const isVisibleRef = useRef<boolean>(true);
    const [isWidgetsModalOpen, setIsWidgetsModalOpen] = useState(false);
    const [isShowDeliveredCard, setIsShowDeliveredCard] = useState(true);

    const radarWidgets = useMemo(() => transformRadarWidgets(user?.radarWidgetSettings ?? DEFAULT_RADAR_WIDGET_SETTINGS), [user?.radarWidgetSettings]);

    const sidebarRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!id) return;
        fetchOrderDetail(id);

        return () => {
            clearOrderDetail();
        };
    }, [id]);

    // Also hydrate orders.slice — the header action buttons (OrderHeaderActions) and the
    // modals they open (Edit/Cancel/Notification Settings) read the order from that slice,
    // exactly like the order detail page. The radar endpoint doesn't return isPriority /
    // textNotification / cancelRequest, so this parallel fetch is the source of truth for them.
    useEffect(() => {
        if (!id) return;
        // context: 'tracking' → the backend records this visit as tracking_viewed only (the radar
        // endpoint above already does), not as an order view too.
        dispatch(getOrderDetailsAsync({ id, context: 'tracking' }));

        return () => {
            dispatch(resetOrderDetailsState());
        };
    }, [id, dispatch]);

    // Live sync with the WAL side: ops approving / rejecting / dismissing one of this
    // customer's requests — or posting a new location after approving a location-update
    // request — re-reads the page in the background, no hard refresh. Both slices the page
    // renders from are refreshed, and both calls are silent so nothing blinks into
    // skeletons, the leg pager stays where the customer put it, and no toast fires.
    const handleLiveSyncRefresh = useCallback(() => {
        if (!id) return;

        fetchOrderDetail(id, { silent: true });
        dispatch(getOrderDetailsAsync({ id, context: 'tracking', silent: true }));
    }, [id, dispatch, fetchOrderDetail]);

    useOrderLiveSync(id, handleLiveSyncRefresh);

    useEffect(() => {
        if (trackOrderDetail?.orderStatus) {
            dispatch(setActiveScheduleSection(null));
        }
    }, [trackOrderDetail?.orderStatus, dispatch]);

    const handleSaveRadarWidgets = useCallback(
        async (updatedWidgets: IWidget[]): Promise<void> => {
            const widgetsObject = transformWidgetsToApiFormat(updatedWidgets);
            const payload = { type: WIDGET_SECTION_TYPE.RADAR, settings: widgetsObject };
            const response = await updateUserWidgets(payload);

            dispatch(
                setCurrentUser({
                    ...user,
                    radarWidgetSettings: response?.data?.radarWidgetSettings ?? widgetsObject,
                })
            );
        },
        [dispatch, user]
    );

    // Padding accounts for floating header/zoom controls and the left sidebar overlay
    // Matches wal-frontend-v2's Radar detail padding so both surfaces frame the
    // route identically — enough top room that the destination marker's label
    // clears the floating header, and enough left room to clear the sidebar.
    const getMapFitPadding = (panelVisible: boolean) => ({
        top: 100,
        right: 100,
        bottom: 60,
        left: panelVisible ? 590 : 80,
    });

    const fitMapToRoute = useCallback(() => {
        if (!mapRef.current || !routeBoundsRef.current) return;
        mapRef.current.fitBounds(routeBoundsRef.current, {
            padding: getMapFitPadding(isVisibleRef.current),
            // maxZoom only caps how close fitBounds can go — long routes still fit wide.
            // 11 lets short local routes (e.g. Saint-Laurent → Montréal) show at city
            // level instead of a tiny blob, while the camera stays framed on the route.
            maxZoom: 11,
        });
    }, []);

    // Re-fit whenever the sidebar visibility changes so the route stays centered
    useEffect(() => {
        isVisibleRef.current = isVisible;
        fitMapToRoute();
    }, [isVisible, fitMapToRoute]);

    // ── Marker factories ───────────────────────────────────────────────────────

    // Default purple circle — used for shipper/consignee when order is booked/dispatched
    const createStartMarker = useCallback(() => {
        const el = document.createElement('div');
        el.innerHTML = `
            <svg width="32" height="37" viewBox="0 0 32 37" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g filter="url(#filter_portal_start)">
                    <path d="M13.1131 7.85152C18.4392 6.39047 23.9413 9.5237 25.4023 14.8498C26.8633 20.1759 23.7301 25.6779 18.404 27.139C13.0779 28.6 7.57589 25.4668 6.11484 20.1407C4.65379 14.8146 7.78702 9.31257 13.1131 7.85152Z" fill="#6172F3"/>
                    <path d="M24.4379 15.1143C25.7529 19.9078 22.933 24.8597 18.1395 26.1746C13.346 27.4895 8.39416 24.6696 7.07921 19.8762C5.76427 15.0827 8.58418 10.1308 13.3777 8.81589C18.1711 7.50095 23.123 10.3209 24.4379 15.1143Z" stroke="white" stroke-width="2"/>
                </g>
                <defs>
                    <filter id="filter_portal_start" x="-1.88742" y="-1.9368" width="35.2924" height="40.6496" filterUnits="userSpaceOnUse">
                        <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                        <feOffset dy="3.57143"/>
                        <feGaussianBlur stdDeviation="1.78571"/>
                        <feComposite in2="hardAlpha" operator="out"/>
                        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"/>
                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1"/>
                        <feBlend mode="normal" in="SourceGraphic" in2="effect1" result="shape"/>
                    </filter>
                </defs>
            </svg>`;

        return new mapboxgl.Marker(el);
    }, []);

    // Blue pin — used for consignee when order is not yet completed
    const createEndMarker = useCallback(() => {
        const el = document.createElement('div');
        el.innerHTML = `
            <svg width="30" height="30" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M25 26.877C28.1066 26.877 30.625 24.3586 30.625 21.252C30.625 18.1454 28.1066 15.627 25 15.627C21.8934 15.627 19.375 18.1454 19.375 21.252C19.375 24.3586 21.8934 26.877 25 26.877Z" fill="#444CE7"/>
                <path d="M25 43.752C32.5 36.252 40 29.5362 40 21.252C40 12.9677 33.2843 6.25195 25 6.25195C16.7157 6.25195 10 12.9677 10 21.252C10 29.5362 17.5 36.252 25 43.752Z" fill="#444CE7"/>
            </svg>`;

        return new mapboxgl.Marker(el);
    }, []);

    // Purple-400 small dot — start marker when in-transit with a live location
    const createStartMarkerActive = useCallback(() => {
        const el = document.createElement('div');
        el.style.cssText = [
            'width: 14px',
            'height: 14px',
            'border-radius: 50%',
            'border: 2px solid #FFF',
            'background: #9B8AFB',
            'box-shadow: 0 3.571px 3.571px 0 rgba(0,0,0,0.10), 0 3.571px 7.143px -1.786px rgba(16,24,40,0.10)',
        ].join('; ');

        return new mapboxgl.Marker(el);
    }, []);

    // Purple-600 small dot — end marker when order is fully completed
    const createEndMarkerActive = useCallback(() => {
        const el = document.createElement('div');
        el.style.cssText = [
            'width: 14px',
            'height: 14px',
            'border-radius: 50%',
            'border: 2px solid #FFF',
            'background: #7A5AF8',
            'box-shadow: 0 3.571px 3.571px 0 rgba(0,0,0,0.10), 0 3.571px 7.143px -1.786px rgba(16,24,40,0.10)',
        ].join('; ');

        return new mapboxgl.Marker(el);
    }, []);

    // Junction dot — placed at the consignee of each intermediate leg. Sized up to 16px
    // (larger than the 14px start/end dots) so stops read clearly against the route line.
    const createWaypointMarker = useCallback(() => {
        const el = document.createElement('div');
        el.style.cssText = [
            'width: 16px',
            'height: 16px',
            'border-radius: 50%',
            'border: 2px solid #FFF',
            'background: #6172F3',
            'box-shadow: 0 3.571px 3.571px 0 rgba(0,0,0,0.10), 0 3.571px 7.143px -1.786px rgba(16,24,40,0.10)',
        ].join('; ');

        return new mapboxgl.Marker(el);
    }, []);

    // Animated arrow truck marker
    const createCurrentMarker = useCallback(() => {
        const el = document.createElement('div');
        el.className = 'parcel-arrow z-[1]';
        el.innerHTML = `
            <div class="w-[12px] -mt-px">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" class="w-full" id="marker-arrow-svg">
                    <path d="M12.6657 2.67916C12.2439 2.4686 11.7476 2.4686 11.3258 2.67916C10.9525 2.86552 10.7595 3.19367 10.6676 3.36083C10.5675 3.543 10.4649 3.77692 10.362 4.01162L3.05073 20.6813C2.93566 20.9436 2.82421 21.1976 2.7529 21.4084C2.69129 21.5905 2.56638 21.9837 2.70011 22.4066C2.84758 22.873 3.21319 23.2382 3.67975 23.3851C4.10282 23.5184 4.49584 23.393 4.67791 23.3312C4.88863 23.2596 5.14253 23.1478 5.4047 23.0324L11.9957 20.1324L18.5868 23.0325C18.8489 23.1478 19.1028 23.2596 19.3135 23.3312C19.4956 23.393 19.8886 23.5184 20.3117 23.3851C20.7783 23.2382 21.1439 22.873 21.2913 22.4066C21.4251 21.9837 21.3002 21.5905 21.2385 21.4084C21.1672 21.1976 21.0558 20.9436 20.9407 20.6813L13.6295 4.01167C13.5266 3.77695 13.424 3.54301 13.3238 3.36083C13.2319 3.19367 13.039 2.86552 12.6657 2.67916Z" fill="white"/>
                </svg>
            </div>`;

        return new mapboxgl.Marker(el);
    }, []);

    const clearMarkers = useCallback(() => {
        markersRef.current.completed.forEach((m) => m.remove());
        markersRef.current.completed = [];

        if (markersRef.current.current) {
            markersRef.current.current.remove();
            markersRef.current.current = null;
        }
    }, []);

    const findClosestCoord = useCallback((targetCoord: [number, number], routeCoords: [number, number][]) => {
        let minDist = Infinity;
        let closest = routeCoords[0];
        routeCoords.forEach((coord) => {
            const dist = turf.distance(turf.point(targetCoord), turf.point(coord), { units: 'meters' });

            if (dist < minDist) {
                minDist = dist;
                closest = coord;
            }
        });

        return closest;
    }, []);

    const findClosestIndex = useCallback((targetCoord: [number, number], coordsArray: [number, number][]) => {
        let minDist = Infinity;
        let closestIdx = 0;
        coordsArray.forEach((coord, idx) => {
            const dist = turf.distance(turf.point(targetCoord), turf.point(coord), { units: 'meters' });

            if (dist < minDist) {
                minDist = dist;
                closestIdx = idx;
            }
        });

        return closestIdx;
    }, []);

    // Fetch a driving route from Mapbox Directions API across multiple points.
    // Delegates to the shared segmented fetcher (25-coord chunking + max-distance/NoRoute
    // pairwise splits) so long routes don't 422 like the raw single-request version did.
    // Returns coords + the API's route distance so Remaining Distance can fall back to
    // the exact same number as Total Distance when nothing has moved yet.
    const getDrivingRoute = useCallback(
        async (start: { lat: number; lng: number }, waypoints: { lat: number; lng: number }[], end: { lat: number; lng: number }, preventCrossBorder: boolean = false): Promise<DrivingRouteResult> =>
            getDrivingRouteWithWaypoints(start, waypoints, end, preventCrossBorder, mapboxgl.accessToken),
        []
    );

    // ── Build driving route, draw lines, and place all markers ────────────────
    const updateRouteAndMarkers = useCallback(async () => {
        if (!mapRef.current || !mapReadyRef.current || !trackOrderDetail) return;

        const legs = trackOrderDetail?.legs ?? [];
        if (legs.length === 0) return;

        const firstLeg = legs[0];
        const lastLeg = legs[legs.length - 1];

        const shipperLat = Number(firstLeg?.shipper?.latitude);
        const shipperLng = Number(firstLeg?.shipper?.longitude);
        const consigneeLat = Number(lastLeg?.consignee?.latitude);
        const consigneeLng = Number(lastLeg?.consignee?.longitude);

        if (!shipperLat || !shipperLng || !consigneeLat || !consigneeLng) return;

        // Tear down only the truck marker and animation; leave shipper/consignee markers
        // in place to prevent tooltip blink during status-only changes.
        if (markersRef.current.current) {
            markersRef.current.current.remove();
            markersRef.current.current = null;
        }

        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }

        if (animationTimeoutRef.current) {
            clearTimeout(animationTimeoutRef.current);
            animationTimeoutRef.current = null;
        }

        // Bump-and-capture so stale Directions API fetches can be discarded. MUST bump (not just
        // read): two overlapping calls that captured the same id would both pass the staleness
        // check below, each add an arrow marker, and the second would overwrite
        // markersRef.current.current — orphaning the first arrow on the map with no way to remove it.
        //
        // Bumped only AFTER the teardown above: bumping before the early returns would advance the
        // counter, killing the running animation loop, while leaving the old arrow on the map with
        // no pending redraw to remove it — a frozen marker over the previous order's route.
        const requestId = bumpTrackRouteRequestSeq();

        const orderStatus = trackOrderDetail?.orderStatus;
        const isBookedOrDispatched = [ORDER_STATUS.BOOKED, ORDER_STATUS.DISPATCHED].includes(orderStatus);
        const hasLocationUpdates = !isBookedOrDispatched;

        // Collect location update waypoints for accurate routing — STARTED legs only.
        // Legs still booked/dispatched can carry stale/pre-entered manual pings; including
        // them teleports the route ahead of the freight (marker at the destination, whole
        // route painted traveled) the moment the master order turns in-transit.
        const allLocationUpdates = hasLocationUpdates ? getStartedLegLocationUpdates(legs) : [];

        // Cross-border routing: exclude country_border when origin and destination are in the same country
        const shipperCountry = firstLeg?.shipper?.country;
        const consigneeCountry = lastLeg?.consignee?.country;
        const isSameCountry = Boolean(shipperCountry && consigneeCountry && shipperCountry === consigneeCountry);
        const hasOutOfCountry = allLocationUpdates.some((u: any) => u?.country && u.country !== shipperCountry);
        const preventCrossBorder = isSameCountry && !hasOutOfCountry;

        // Truck position = the newest ping BY TIMESTAMP across STARTED legs. The old scan
        // (last leg → first, first hit wins) let a stale ping on a not-yet-started later leg
        // override the active leg's fresh location.
        const lastLocation: any = getLatestStartedLegLocation(legs);

        const hasRealLocation = Boolean(lastLocation?.longitude) && !isBookedOrDispatched;

        const activeShipperLng = hasRealLocation ? Number(lastLocation?.longitude) : shipperLng;
        const activeShipperLat = hasRealLocation ? Number(lastLocation?.latitude) : shipperLat;

        // Fetch the driving route through all location-update waypoints
        const {
            coords: allCoordinates,
            distanceMiles: routeDistanceMiles,
            measureCoords,
        } = await getDrivingRoute(
            { lat: shipperLat, lng: shipperLng },
            allLocationUpdates.map((u: any) => ({ lat: Number(u.latitude), lng: Number(u.longitude) })),
            { lat: consigneeLat, lng: consigneeLng },
            preventCrossBorder
        );

        // Discard stale fetch: another updateRouteAndMarkers call was made while this was awaiting
        if (requestId !== getTrackRouteRequestSeq()) return;

        // Component may have unmounted while the fetch was in-flight
        if (!mapRef.current || !mapReadyRef.current) return;

        if (allCoordinates.length === 0) return;

        // ── Compute traveled (purple) vs remaining (blue) split ───────────────
        const isCompletedOrder = ![ORDER_STATUS.BOOKED, ORDER_STATUS.DISPATCHED, ORDER_STATUS.IN_TRANSIT].includes(orderStatus);

        let traveledCoords: [number, number][] = [];
        let remainingCoords: [number, number][] = allCoordinates;
        let splitIndex = 0;

        if (isCompletedOrder) {
            traveledCoords = allCoordinates;
            remainingCoords = [];
        } else if (hasRealLocation) {
            const snappedSplit = findClosestCoord([activeShipperLng, activeShipperLat], allCoordinates);
            splitIndex = findClosestIndex(snappedSplit, allCoordinates);
            traveledCoords = allCoordinates.slice(0, splitIndex + 1);
            remainingCoords = allCoordinates.slice(splitIndex);
        }

        // ── Remaining distance (mirrors WAL radar) ─────────────────────────────
        // Completed: 0. In-transit with location: truck → consignee measured along the
        // route. No pings yet: nothing has moved, so reuse Mapbox's full-route distance
        // instead of re-measuring with turf — a turf re-measure drifts a mile or two and
        // would make the Total and Remaining cards disagree at 0% progress.
        if (allCoordinates.length > 1) {
            if (isCompletedOrder) {
                dispatch(setRemainingDistance(0));
            } else if (hasRealLocation && remainingCoords.length > 1) {
                // Measure on the UNSIMPLIFIED geometry (measureCoords), not the render polyline:
                // simplifyForRender cuts corners on 10k+ point routes and read a mile short of WAL
                // (572 vs 573) for the same order. Fall back to the drawn line only if the raw
                // geometry is unavailable.
                const rawRemainingMiles = getRemainingRouteMiles(measureCoords, [activeShipperLng, activeShipperLat]);
                const remainingLine = turf.lineString(remainingCoords);
                dispatch(setRemainingDistance(rawRemainingMiles ?? parseFloat(turf.length(remainingLine, { units: 'miles' }).toFixed(2))));
            } else if (hasRealLocation) {
                // Split landed on the route's final point (single-coord remainder) — the
                // truck is at the destination, so remaining is 0, not the full-route
                // fallback below (which would show Remaining = Total).
                dispatch(setRemainingDistance(0));
            } else {
                dispatch(setRemainingDistance(routeDistanceMiles));
            }
        }

        // ── Draw or update route line layers ──────────────────────────────────
        const traveledGeoJson = {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: traveledCoords.length > 1 ? traveledCoords : [] },
        } as any;

        if (mapRef.current?.getSource('route-traveled')) {
            (mapRef.current.getSource('route-traveled') as mapboxgl.GeoJSONSource).setData(traveledGeoJson);
        } else {
            mapRef.current?.addSource('route-traveled', { type: 'geojson', data: traveledGeoJson } as any);
            mapRef.current?.addLayer({
                id: 'route-traveled-line',
                type: 'line',
                source: 'route-traveled',
                paint: { 'line-color': '#7A5AF8', 'line-width': 6, 'line-opacity': 0.5 },
            });
        }

        const remainingGeoJson = {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: remainingCoords },
        } as any;

        if (mapRef.current?.getSource('route-remaining')) {
            (mapRef.current.getSource('route-remaining') as mapboxgl.GeoJSONSource).setData(remainingGeoJson);
        } else {
            mapRef.current?.addSource('route-remaining', { type: 'geojson', data: remainingGeoJson } as any);
            mapRef.current?.addLayer({
                id: 'route-remaining-line',
                type: 'line',
                source: 'route-remaining',
                paint: { 'line-color': '#444CE7', 'line-width': 6 },
            });
        }

        // IN_TRANSIT but no GPS location update yet — hide the static shipper (start) marker and
        // render the animated location arrow at the shipper point instead (see arrow block below).
        const isInTransitNoLocation = orderStatus === ORDER_STATUS.IN_TRANSIT && !hasRealLocation;

        // ── Shipper / consignee markers — only rebuild when style changes ─────
        const styleKey = isBookedOrDispatched ? 'default' : isCompletedOrder ? 'completed' : hasRealLocation ? 'inTransit' : isInTransitNoLocation ? 'inTransitNoLocation' : 'default';
        const needsRebuild = completedMarkerStyleRef.current !== styleKey || markersRef.current.completed.length === 0;

        if (needsRebuild) {
            markersRef.current.completed.forEach((m) => m.remove());
            markersRef.current.completed = [];

            // null start marker → in-transit with no GPS location: the animated location arrow
            // (drawn below) stands in for the shipper point, so we skip the static start marker.
            let startM: mapboxgl.Marker | null;
            let endM: mapboxgl.Marker;

            if (isBookedOrDispatched) {
                startM = createStartMarker();
                endM = createStartMarker();
            } else if (isCompletedOrder) {
                startM = createStartMarkerActive();
                endM = createEndMarkerActive();
            } else if (hasRealLocation) {
                startM = createStartMarkerActive();
                endM = createStartMarker();
            } else if (isInTransitNoLocation) {
                startM = null;
                endM = createStartMarker();
            } else {
                startM = createStartMarker();
                endM = createStartMarker();
            }

            // Shipper marker with city/state tooltip
            if (startM) {
                startM.setLngLat([allCoordinates[0][0], allCoordinates[0][1]]).addTo(mapRef.current!);
                const shipperLabel = [firstLeg?.shipper?.city, firstLeg?.shipper?.stateCode].filter(Boolean).join(', ');

                if (shipperLabel) {
                    const popup = new mapboxgl.Popup({
                        offset: [0, -12],
                        closeButton: false,
                        className: 'map-marker-popup',
                        closeOnClick: false,
                        anchor: 'bottom',
                    }).setHTML(
                        `<div style="font-size:12px;font-weight:500;color:#F9F9F9;background:#0C111B;padding:4px 8px;border-radius:8px;border:1px solid #0C111B;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,0.15);line-height:1.4;">${shipperLabel}</div>`
                    );
                    startM.setPopup(popup).togglePopup();
                }
            }

            // Consignee marker snapped to nearest route coord, with city/state tooltip
            const snappedEnd = findClosestCoord([consigneeLng, consigneeLat], allCoordinates);
            endM.setLngLat(snappedEnd).addTo(mapRef.current!);
            const consigneeLabel = [lastLeg?.consignee?.city, lastLeg?.consignee?.stateCode].filter(Boolean).join(', ');

            if (consigneeLabel) {
                const popup = new mapboxgl.Popup({
                    offset: [0, -12],
                    closeButton: false,
                    className: 'map-marker-popup',
                    closeOnClick: false,
                    anchor: 'bottom',
                }).setHTML(
                    `<div style="font-size:12px;font-weight:500;color:#F9F9F9;background:#0C111B;padding:4px 8px;border-radius:8px;border:1px solid #0C111B;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,0.15);line-height:1.4;">${consigneeLabel}</div>`
                );
                endM.setPopup(popup).togglePopup();
            }

            if (startM) {
                markersRef.current.completed.push(startM);
            }

            markersRef.current.completed.push(endM);

            // Intermediate leg junction dots — placed at each leg's consignee, with the same
            // always-visible city/state tooltip the shipper/consignee markers get so stops
            // are identifiable without hovering.
            if (legs.length > 1) {
                for (let i = 0; i < legs.length - 1; i++) {
                    const jLat = Number(legs[i]?.consignee?.latitude);
                    const jLng = Number(legs[i]?.consignee?.longitude);

                    if (!jLat || !jLng) continue;

                    const snapped = findClosestCoord([jLng, jLat], allCoordinates);
                    const waypointM = createWaypointMarker();
                    waypointM.setLngLat(snapped).addTo(mapRef.current!);
                    const junctionLabel = [legs[i]?.consignee?.city, legs[i]?.consignee?.stateCode].filter(Boolean).join(', ');

                    if (junctionLabel) {
                        const popup = new mapboxgl.Popup({
                            offset: [0, -12],
                            closeButton: false,
                            className: 'map-marker-popup',
                            closeOnClick: false,
                            anchor: 'bottom',
                        }).setHTML(
                            `<div style="font-size:12px;font-weight:500;color:#F9F9F9;background:#0C111B;padding:4px 8px;border-radius:8px;border:1px solid #0C111B;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,0.15);line-height:1.4;">${junctionLabel}</div>`
                        );
                        waypointM.setPopup(popup).togglePopup();
                    }

                    markersRef.current.completed.push(waypointM);
                }
            }

            completedMarkerStyleRef.current = styleKey;
        }

        // ── Animated truck marker ─────────────────────────────────────────────
        // - Live GPS location exists → arrow snaps to that location.
        // - IN_TRANSIT with no GPS location yet → arrow stands in for the (hidden) shipper marker,
        //   starting at the shipper point and animating forward along the route.
        if (orderStatus === ORDER_STATUS.IN_TRANSIT) {
            // activeShipperLng/Lat already falls back to the shipper point when there is no GPS ping,
            // so this snaps to the route start for the no-location case.
            const snappedCoords = findClosestCoord([activeShipperLng, activeShipperLat], allCoordinates);

            const animateMarkerAlongLine = (marker: mapboxgl.Marker, routeCoords: [number, number][], startIdx: number) => {
                const remainingRouteCoords = routeCoords.slice(startIdx);

                // Truck already at/next to the destination: fewer than 2 coords remain past
                // the split point — turf.lineString throws on <2 positions and there is
                // nothing left to animate along anyway.
                if (remainingRouteCoords.length < 2) return;

                const line = turf.lineString(remainingRouteCoords);
                const routeLength = turf.length(line, { units: 'meters' });
                const speedMetersPerSecond = 1;
                const distanceThresholdMeters = 50000;
                let distanceAlongRoute = 0;
                let lastTimestamp: number | null = null;
                const originalTraveledCoords = routeCoords.slice(0, startIdx + 1);

                const step = (timestamp?: number) => {
                    // Zombie-loop kill switch: a newer route draw (or the list view taking the map
                    // back) supersedes this loop. Without this it keeps rewriting route-traveled /
                    // route-remaining and fights the new loop every frame.
                    if (requestId !== getTrackRouteRequestSeq() || getActiveTrackView() !== 'detail') return;

                    if (!timestamp) {
                        animationFrameRef.current = requestAnimationFrame(step);

                        return;
                    }

                    if (!lastTimestamp) {
                        lastTimestamp = timestamp;
                        animationFrameRef.current = requestAnimationFrame(step);

                        return;
                    }

                    const elapsed = (timestamp - lastTimestamp) / 1000;
                    lastTimestamp = timestamp;
                    distanceAlongRoute += speedMetersPerSecond * elapsed;

                    if (distanceAlongRoute >= distanceThresholdMeters) distanceAlongRoute = 0;

                    const routeLengthKm = routeLength / 1000;
                    const currentPoint = turf.along(line, distanceAlongRoute / 1000, { units: 'kilometers' });
                    const coordsData = currentPoint.geometry.coordinates as [number, number];

                    const nextDist = Math.min(distanceAlongRoute + 5, routeLength * 1000);
                    const nextPoint = turf.along(line, nextDist / 1000, { units: 'kilometers' });
                    const nextCoords = nextPoint.geometry.coordinates as [number, number];
                    const bearing = turf.bearing(turf.point(coordsData), turf.point(nextCoords));

                    marker.setLngLat(coordsData);
                    const svgArrow = marker.getElement().querySelector('#marker-arrow-svg') as HTMLElement;

                    if (svgArrow) svgArrow.style.transform = `rotate(${bearing}deg)`;

                    currentMarkerPositionRef.current = coordsData;

                    // Grow traveled / shrink remaining lines in sync with the animated marker
                    if (mapRef.current?.getSource('route-traveled') && mapRef.current?.getSource('route-remaining')) {
                        const distKm = Math.max(0, Math.min(distanceAlongRoute / 1000, routeLengthKm));

                        if (distKm > 0 && routeLengthKm > 0) {
                            const animTraveled = turf.lineSliceAlong(line, 0, distKm, { units: 'kilometers' }).geometry.coordinates;
                            const animRemaining = distKm < routeLengthKm ? turf.lineSliceAlong(line, distKm, routeLengthKm, { units: 'kilometers' }).geometry.coordinates : [];
                            const fullTraveled = [...originalTraveledCoords, ...animTraveled.slice(1)];

                            (mapRef.current.getSource('route-traveled') as mapboxgl.GeoJSONSource).setData({
                                type: 'Feature',
                                geometry: { type: 'LineString', coordinates: fullTraveled.length > 1 ? fullTraveled : [] },
                            } as any);

                            (mapRef.current.getSource('route-remaining') as mapboxgl.GeoJSONSource).setData({
                                type: 'Feature',
                                geometry: { type: 'LineString', coordinates: animRemaining.length > 1 ? animRemaining : [] },
                            } as any);
                        }
                    }

                    animationFrameRef.current = requestAnimationFrame(step);
                };

                animationFrameRef.current = requestAnimationFrame(step);
            };

            const currentMarker = createCurrentMarker();
            currentMarker.setLngLat(snappedCoords).addTo(mapRef.current!);
            markersRef.current.current = currentMarker;

            animationTimeoutRef.current = window.setTimeout(() => {
                animationTimeoutRef.current = null;

                // Bail if a newer draw started or the list took the map back while this
                // delayed start was pending (same guard as the Directions fetch).
                if (requestId !== getTrackRouteRequestSeq() || getActiveTrackView() !== 'detail') return;

                animateMarkerAlongLine(currentMarker, allCoordinates, splitIndex);
            }, 1000) as unknown as number;
        }

        // ── Fit map to show the full route ────────────────────────────────────
        const bounds = new mapboxgl.LngLatBounds();
        allCoordinates.forEach((coord) => bounds.extend(coord));

        if (lastLocation?.latitude && lastLocation?.longitude) {
            bounds.extend([Number(lastLocation.longitude), Number(lastLocation.latitude)]);
        }

        routeBoundsRef.current = bounds;
        fitMapToRoute();
    }, [
        trackOrderDetail,
        dispatch,
        getDrivingRoute,
        findClosestCoord,
        findClosestIndex,
        createStartMarker,
        createEndMarker,
        createStartMarkerActive,
        createEndMarkerActive,
        createWaypointMarker,
        createCurrentMarker,
        fitMapToRoute,
    ]);

    // Keep a ref to always call the latest updateRouteAndMarkers from the setup
    // effect without listing it as a dependency (which would cause setup to re-run
    // on every orderDetail change, redundantly with the dedicated data effect).
    const updateRouteAndMarkersRef = useRef(updateRouteAndMarkers);
    useEffect(() => {
        updateRouteAndMarkersRef.current = updateRouteAndMarkers;
    });

    // A theme change (map.setStyle) destroys this page's route sources/layers.
    // TrackMapLayout bumps styleEpoch once the new style has loaded; redraw then.
    //
    // Safe to call directly: updateRouteAndMarkers only RECORDS the route bounds
    // (routeBoundsRef) and never moves the camera itself, so switching theme
    // redraws the route without yanking the user's viewport. Markers are cleared
    // first and completedMarkerStyleRef reset so the styleKey guard doesn't skip
    // the rebuild. Read through the ref so this never calls a stale closure.
    useEffect(() => {
        if (!styleEpoch) return;
        if (!mapRef.current) return;
        if (getActiveTrackView() !== 'detail') return;

        clearMarkers();
        completedMarkerStyleRef.current = '';
        void updateRouteAndMarkersRef.current();
    }, [styleEpoch, mapRef, clearMarkers]);

    // ── Reset per-order state on id change or unmount ─────────────────────────
    useEffect(() => {
        currentMarkerPositionRef.current = null;
        completedMarkerStyleRef.current = '';

        return () => {
            clearMarkers();

            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }

            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
                animationTimeoutRef.current = null;
            }

            // Signal that this mount's map setup is no longer valid. The next
            // setup effect (for the new id) will flip it back to true.
            mapReadyRef.current = false;
            routeBoundsRef.current = null;
        };
    }, [id, clearMarkers]);

    // ── Attach persistent map, clean residue, draw route ─────────────────────
    // Runs on: id change, mapRef availability change, isMapReady flip.
    // Guards ensure we don't attempt drawing before the map style is loaded.
    useEffect(() => {
        if (!mapContainerRef.current || !mapRef.current) return;

        // Claim ownership so the list's setOrderMarker retries see != 'list' and bail.
        setActiveTrackView('detail');
        // Invalidate any in-flight Directions fetch from a previous mount so a slow
        // response doesn't overwrite the current order's route geometry.
        bumpTrackRouteRequestSeq();

        attachMapTo(mapContainerRef.current);

        if (!isMapReady) return;

        // Wipe any residue left by the list view (or a previous detail view).
        try {
            // List residue: empty cluster source + remove DOM markers + popups
            const ordersSource = mapRef.current.getSource('orders') as mapboxgl.GeoJSONSource | undefined;

            if (ordersSource) {
                ordersSource.setData({ type: 'FeatureCollection', features: [] });
            }

            if ((mapRef.current as any)._clearAllMarkers) {
                (mapRef.current as any)._clearAllMarkers();
            }

            document.querySelectorAll('.mapboxgl-popup').forEach((el) => el.remove());
            document.querySelectorAll('.parent-marker').forEach((el) => el.remove());

            // Previous detail residue: remove stale route layers before redrawing
            if (mapRef.current.getLayer('route-traveled-line')) mapRef.current.removeLayer('route-traveled-line');
            if (mapRef.current.getLayer('route-remaining-line')) mapRef.current.removeLayer('route-remaining-line');
            if (mapRef.current.getSource('route-traveled')) mapRef.current.removeSource('route-traveled');
            if (mapRef.current.getSource('route-remaining')) mapRef.current.removeSource('route-remaining');
        } catch {
            // Map may be mid-transition — ignore; updateRouteAndMarkers will retry
        }

        // Also clear this detail's own marker refs so we don't double-add when
        // updateRouteAndMarkers runs below.
        clearMarkers();
        completedMarkerStyleRef.current = '';

        mapReadyRef.current = true;
        mapRef.current.resize();
        // Call via ref so we always use the latest orderDetail even if it arrived
        // after the map style was already loaded.
        updateRouteAndMarkersRef.current();

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }

            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
                animationTimeoutRef.current = null;
            }
        };
    }, [id, mapRef, attachMapTo, isMapReady]); // Re-run on :id change or once the persistent map finishes loading

    // ── On full unmount: signal list, release ownership, hand map back ────────
    useEffect(
        () => () => {
            // Signal the list view that it's returning from a detail page so it
            // uses the smooth ease-back animation instead of a snap.
            setCameFromDetail(true);

            // Release source ownership so the list's setOrderMarker can run again.
            if (getActiveTrackView() === 'detail') {
                setActiveTrackView(null);
            }

            // Invalidate any in-flight Directions fetch this mount started
            bumpTrackRouteRequestSeq();

            // Remove route line layers + sources so the route doesn't bleed through
            // onto the cluster/marker view when the user returns to the list. The
            // detail's setup effect cleans these when mounting for a NEW order, but
            // the list page has no cleanup for them — so we own this here.
            if (mapRef.current) {
                try {
                    if (mapRef.current.getLayer('route-traveled-line')) mapRef.current.removeLayer('route-traveled-line');
                    if (mapRef.current.getLayer('route-remaining-line')) mapRef.current.removeLayer('route-remaining-line');
                    if (mapRef.current.getSource('route-traveled')) mapRef.current.removeSource('route-traveled');
                    if (mapRef.current.getSource('route-remaining')) mapRef.current.removeSource('route-remaining');
                } catch {
                    // Map may be mid-transition — safe to ignore
                }
            }

            // Hand the persistent map's DOM node back to the layout stash
            returnToStash();
        },
        [returnToStash]
    );

    // Update route and markers whenever order data changes (handles the case where
    // orderDetail arrives before or after the map style is ready)
    useEffect(() => {
        if (mapRef.current && mapReadyRef.current) {
            updateRouteAndMarkers();
        }
    }, [trackOrderDetail?.orderStatus, trackOrderDetail?.legs, trackOrderDetail?.id]);

    const handleRecenterMap = useCallback(() => {
        fitMapToRoute();
    }, [fitMapToRoute]);

    // Jump the camera in close on the live truck arrow. Prefers the animated
    // position ref (kept in step with the marker as it interpolates along the
    // route); falls back to the marker's own LngLat when the ref hasn't been
    // populated yet — e.g. the very first frame after a redraw.
    //
    // fitBounds on a single point + maxZoom is how WAL does it: it reuses the same
    // sidebar-aware padding as the recenter button, so the arrow lands in the
    // visible part of the map rather than behind the order panel.
    const handleZoomToTruck = useCallback(() => {
        if (!mapRef.current) return;

        const arrowPos = currentMarkerPositionRef.current || (markersRef.current.current ? markersRef.current.current.getLngLat().toArray() : null);

        if (!arrowPos) return;

        const bounds = new mapboxgl.LngLatBounds();
        bounds.extend(arrowPos as [number, number]);

        mapRef.current.fitBounds(bounds, {
            padding: getMapFitPadding(isVisibleRef.current),
            maxZoom: 17,
            // Instant jump — no smooth camera glide.
            animate: false,
        });
    }, [mapRef]);

    const handleNavigateBack = useCallback(() => {
        navigate(PATH.TRACK);
    }, [navigate]);

    return (
        <>
            <PageSectionLayout header={<></>} headerClassName="!p-0" contentClassName="!p-0 relative">
                {/* ── Full-screen map background ── */}
                <div className="relative h-full w-full radar-mapbox z-[1]">
                    <style>{`
                        .current-location-marker { width: 24px; height: 24px; cursor: pointer; }
                        .radar-mapbox .mapboxgl-ctrl-top-right { padding-top: 68px; padding-right: 8px; }
                        .map-marker-popup .mapboxgl-popup-content { padding: 0; background: transparent; box-shadow: none; }
                        .map-marker-popup .mapboxgl-popup-tip { display: none; }
                    `}</style>

                    <div id="map" ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />

                    {trackOrderDetail && ![ORDER_STATUS.BOOKED, ORDER_STATUS.DISPATCHED, ORDER_STATUS.IN_TRANSIT].includes(trackOrderDetail?.orderStatus) && (
                        <div className="absolute inset-0 bg-white/60 z-[2] pointer-events-none" />
                    )}

                    {mapRef.current && (
                        /* Recenter + Zoom-to-truck — joined into one segmented box (matches the
                            native Mapbox +/- control group): single white rounded card, buttons
                            stacked with a divider, only outer corners rounded. */
                        <div className="absolute z-[12] right-[18px] top-[145px] flex flex-col rounded-md overflow-hidden border border-borderPrimary bg-white shadow-sm divide-y divide-borderPrimary">
                            <TooltipCmp message={t('Radar.Recenter_Map')}>
                                <ButtonCmp
                                    className="!rounded-none !border-0 !shadow-none bg-white hover:bg-gray50"
                                    size="square-sm"
                                    onClick={handleRecenterMap}
                                    icon={<Move className="w-4 h-4 text-textSecondary" />}
                                >
                                    <></>
                                </ButtonCmp>
                            </TooltipCmp>

                            {/* Zoom-to-truck — in-transit only: there is no live arrow marker to
                                zoom to in any other status, so the button would be a no-op. */}
                            {trackOrderDetail?.orderStatus === ORDER_STATUS.IN_TRANSIT && (
                                <TooltipCmp message={t('Radar.Zoom_To_Truck')}>
                                    <ButtonCmp
                                        className="!rounded-none !border-0 !shadow-none bg-white hover:bg-gray50"
                                        size="square-sm"
                                        onClick={handleZoomToTruck}
                                        icon={<Target03 className="w-4 h-4 text-textSecondary" />}
                                    >
                                        <></>
                                    </ButtonCmp>
                                </TooltipCmp>
                            )}
                        </div>
                    )}

                    <RadarMapAboveComponentNew isShowDeliveredCard={isShowDeliveredCard} setIsShowDeliveredCard={setIsShowDeliveredCard} />
                </div>

                {/* ── Absolute overlay: floating header + left sidebar ── */}
                <div className="absolute xxl:px-4 px-3 pt-4 w-full space-y-2 h-full flex flex-col overflow-hidden">
                    {/* Floating header */}
                    <div className={`radar-header-blur z-[2] ${trackHeaderThemeClass}`}>
                        <Header
                            title={
                                <div className="flex items-center justify-between w-[537px] md:gap-2 gap-1 flex-wrap px-2 -mx-2">
                                    <div className="flex items-center md:gap-2 gap-1 flex-wrap">
                                        <ButtonCmp className="btn_secondary_black" size="square-sm" onClick={handleNavigateBack}>
                                            <ArrowLeft className="w-4 h-4" />
                                        </ButtonCmp>
                                        <div className="flex flex-col">
                                            <p className={`${isDetailLoading ? 'custom-loading' : ''}`}>{isDetailLoading ? 'WAL-XXXX-XXXX' : `#${trackOrderDetail?.orderId ?? ''}`}</p>
                                        </div>
                                        {/* Same live clock as the Track list header — every date on this page
                                            reads in the viewer's timezone, so the reference clock belongs here too.
                                            Self-updating each second via useTrackClock; no refresh needed. */}
                                        <TrackHeaderClock className="md:ml-1" />
                                    </div>
                                    {isVisible && (
                                        <TooltipCmp message="Customize Tracking Widgets" childrenClassName="flex">
                                            <ButtonCmp className="btn-outline-primary" size="square-sm" onClick={() => setIsWidgetsModalOpen(true)}>
                                                <Settings04 className="w-4 h-4" />
                                            </ButtonCmp>
                                        </TooltipCmp>
                                    )}
                                </div>
                            }
                            rightSideContent={
                                <div className="flex items-center sm:gap-x-2.5 gap-x-2">
                                    {/* Same order-level actions as the order detail page header —
                                        reads orders.slice, hydrated by the getOrderDetailsAsync effect above.
                                        btn_secondary_black so the cluster matches the icon buttons beside it.
                                        Order Actions dropdown hidden here per design request (2026-09-02) —
                                        flip showOrderActions to re-enable it. */}
                                    <OrderHeaderActions orderDbId={id!} buttonClassName="btn_secondary_black" showOrderActions={false} isIconOnly />

                                    {/* No projection toggle here: the detail page always renders flat
                                        (it forces 'mercator' below), same as wal-frontend-v2. */}
                                    <TrackMapThemePicker buttonClassName="btn_secondary_black" />

                                    <TrackTimezoneToggle buttonClassName="btn_secondary_black" />

                                    <TooltipCmp message={isVisible ? 'Hide Panel' : 'Show Panel'} childrenClassName="flex">
                                        <ButtonCmp
                                            size="square-sm"
                                            className="btn_secondary_black"
                                            icon={isVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                            onClick={() => setIsVisible((prev) => !prev)}
                                        >
                                            <></>
                                        </ButtonCmp>
                                    </TooltipCmp>
                                </div>
                            }
                            mainContainerClassName="z-[2] !bg-transparent"
                        />
                    </div>
                    {/* ── Left sidebar panel ── */}
                    <div
                        ref={sidebarRef}
                        className={`w-[537px] flex-1 overflow-auto scrollbar-hide z-[2] custom-scrollbar-v2 flex flex-col pb-4 px-2 -mx-2 pt-2 space-y-2 transition-transform duration-300 ease-[cubic-bezier(.25,.1,.25,1)] ${
                            isVisible ? 'translate-x-0' : '-translate-x-full'
                        }`}
                    >
                        <OrderMetaCards />

                        <div className="flex flex-col space-y-2">
                            {/*
                             * Which schedule card shows is driven by the switcher's selection
                             * (resolvedScheduleSection), NOT by orderStatus — that's the whole point of
                             * the dropdown. orderStatus only seeds the DEFAULT selection, in
                             * resolvedScheduleSection above. Gating render on orderStatus here made the
                             * dropdown inert: the label changed but the card underneath never did.
                             */}
                            {isDetailLoading || resolvedScheduleSection === 'pickup' ? (
                                <PickupStateSection titleContent={scheduleSwitcher} />
                            ) : resolvedScheduleSection === 'inTransit' ? (
                                <InTransitStateSection titleContent={scheduleSwitcher} />
                            ) : resolvedScheduleSection === 'delivery' && (trackOrderDetail?.orderStatus || navStateOrderStatus) ? (
                                <DeliveredStateSection titleContent={scheduleSwitcher} />
                            ) : null}

                            {/* Widget-controlled sections — ordered and toggled by user settings */}
                            {radarWidgets.map((widget) => (
                                <React.Fragment key={widget.id}>
                                    {widget.id === RADAR_WIDGET_IDS.CARRIER_DETAILS && widget.isEnabled && <CarrierDetailsSection />}
                                    {widget.id === RADAR_WIDGET_IDS.ORDER_DETAILS && widget.isEnabled && <OrderDetailsSection />}
                                    {/* Same gate as wal-frontend-v2's RadarOrderDetail: hidden only pre-pickup (booked/dispatched — no
                                        updates exist yet), shown for every status from in-transit onward so history stays after delivery. */}
                                    {trackOrderDetail?.orderStatus !== ORDER_STATUS.BOOKED &&
                                        trackOrderDetail?.orderStatus !== ORDER_STATUS.DISPATCHED &&
                                        widget.id === RADAR_WIDGET_IDS.LOCATION_UPDATES &&
                                        widget.isEnabled && <LocationUpdatesSection />}
                                    {/* Chat is unavailable for WAL staff sessions (no contact participant row) */}
                                    {widget.id === RADAR_WIDGET_IDS.INTERNAL_CHAT && widget.isEnabled && !user?.isWalUserSession && <InternalChatSection />}
                                    {widget.id === RADAR_WIDGET_IDS.LATEST_ACTIVITY && widget.isEnabled && <LatestActivitySection />}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            </PageSectionLayout>

            {isWidgetsModalOpen && (
                <WidgetSettingsModal
                    title="Tracking Widgets"
                    handleClose={() => setIsWidgetsModalOpen(false)}
                    widgets={radarWidgets.filter(
                        (w) => w.id !== RADAR_WIDGET_IDS.LOCATION_UPDATES || (trackOrderDetail?.orderStatus !== ORDER_STATUS.BOOKED && trackOrderDetail?.orderStatus !== ORDER_STATUS.DISPATCHED)
                    )}
                    onSave={handleSaveRadarWidgets}
                />
            )}
        </>
    );
};

export default RadarOrderDetailNew;
